import { useState } from 'react';
import { pastelFromBackground } from '@/lib/leadPastels';

const REASONS = [
  { id: 'unable_to_reach', label: 'No se pudo contactar' },
  { id: 'inaccurate_data', label: 'Datos inválidos / spam' },
  { id: 'no_need', label: 'Sin interés ni necesidad' },
  { id: 'no_budget', label: 'Sin presupuesto' },
  { id: 'no_fit', label: 'No es el perfil' },
  { id: 'not_ready', label: 'No está listo ahora' },
  { id: 'no_authority', label: 'No decide la compra' },
  { id: 'other', label: 'Otro' },
] as const;

const FITS = [
  { id: 'consumer', label: 'Consumidor' },
  { id: 'no_business', label: 'Sin negocio' },
  { id: 'wrong_geo', label: 'Otro país' },
  { id: 'too_small', label: 'Muy chica' },
] as const;

export type PwaLeadFields = {
  leadQuality: string | null;
  leadReason: string | null;
  leadFitDetails?: string[];
  leadNote: string | null;
  leadRatedSource: string | null;
};

type LeadState = { id: string; label: string; color?: string };

type Props = {
  value: PwaLeadFields;
  canWrite: boolean;
  busy?: boolean;
  states?: LeadState[] | null;
  onSave: (patch: {
    leadQuality: string | null;
    leadReason?: string | null;
    leadFitDetails?: string[];
    leadNote?: string | null;
  }) => Promise<void>;
};

export function PwaLeadQualityBlock({ value, canWrite, busy, states, onSave }: Props) {
  const [draftUnqualified, setDraftUnqualified] = useState(false);
  const quality = draftUnqualified ? 'unqualified' : value.leadQuality;

  return (
    <div className="pwa-info-sheet__lead">
      <div className="pwa-info-sheet__lead-title">
        Lead
        {value.leadRatedSource === 'llm' ? <span> · sugerido por el agente</span> : null}
      </div>
      {states && states.length > 0 ? (
        <div className="pwa-info-sheet__lead-chips">
          {states.map((state, index) => {
            const pastel = pastelFromBackground(state.color, index);
            const active = quality === state.id;
            return (
            <button
              key={state.id}
              type="button"
              className="pwa-chip"
              style={{
                background: pastel.background,
                color: pastel.color,
                borderColor: active ? pastel.color : 'transparent',
              }}
              disabled={!canWrite || busy}
              onClick={() => void onSave({ leadQuality: quality === state.id ? null : state.id })}
            >
              {state.label}
            </button>
            );
          })}
        </div>
      ) : (
      <div className="pwa-info-sheet__lead-chips">
        <button
          type="button"
          className={quality === 'qualified' ? 'pwa-chip pwa-chip--on' : 'pwa-chip'}
          disabled={!canWrite || busy}
          onClick={() => {
            setDraftUnqualified(false);
            void onSave({ leadQuality: quality === 'qualified' ? null : 'qualified' });
          }}
        >
          Calificado
        </button>
        <button
          type="button"
          className={quality === 'unqualified' ? 'pwa-chip pwa-chip--on' : 'pwa-chip'}
          disabled={!canWrite || busy}
          onClick={() => {
            if (quality === 'unqualified') {
              setDraftUnqualified(false);
              void onSave({ leadQuality: null });
              return;
            }
            setDraftUnqualified(true);
          }}
        >
          No calificado
        </button>
      </div>
      )}
      {!states?.length && quality === 'unqualified' ? (
        <div className="pwa-info-sheet__lead-chips">
          {REASONS.map((r) => (
            <button
              key={r.id}
              type="button"
              className={value.leadReason === r.id ? 'pwa-chip pwa-chip--on' : 'pwa-chip'}
              disabled={!canWrite || busy}
              onClick={() => {
                setDraftUnqualified(false);
                void onSave({ leadQuality: 'unqualified', leadReason: r.id });
              }}
            >
              {r.label}
            </button>
          ))}
        </div>
      ) : null}
      {quality === 'unqualified' && value.leadReason === 'no_fit' ? (
        <div className="pwa-info-sheet__lead-chips">
          {FITS.map((f) => {
            const on = (value.leadFitDetails ?? []).includes(f.id);
            return (
              <button
                key={f.id}
                type="button"
                className={on ? 'pwa-chip pwa-chip--on' : 'pwa-chip'}
                disabled={!canWrite || busy}
                onClick={() => {
                  const cur = value.leadFitDetails ?? [];
                  const next = on ? cur.filter((x) => x !== f.id) : [...cur, f.id];
                  void onSave({ leadQuality: 'unqualified', leadReason: 'no_fit', leadFitDetails: next });
                }}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
